import { doc, setDoc, getDoc, collection, query, where, getDocs, orderBy, deleteDoc, addDoc, updateDoc, serverTimestamp, onSnapshot } from "firebase/firestore"
import { db, COLLECTIONS, USER_TYPES, STUDIO_ROLES, INVITE_STATUS } from "../firebase"

// Re-export constants for use in other components
export { STUDIO_ROLES, USER_TYPES, COLLECTIONS, INVITE_STATUS }

// ============================================================================
// USER MANAGEMENT
// ============================================================================

/**
 * Create a user profile in Firestore
 */
export const createUserProfile = async (userId, userData) => {
  try {
    const userProfile = {
      email: userData.email,
      displayName: userData.displayName,
      userType: userData.userType || USER_TYPES.REGULAR,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      profile: {
        avatar: null,
        bio: "",
        preferences: {},
      },
    }

    await setDoc(doc(db, COLLECTIONS.USERS, userId), userProfile)
    return userProfile
  } catch (error) {
    console.error("Error creating user profile:", error)
    throw error
  }
}

/**
 * Get user profile by ID
 */
export const getUserProfile = async userId => {
  try {
    const userDoc = await getDoc(doc(db, COLLECTIONS.USERS, userId))
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() }
    }
    return null
  } catch (error) {
    console.error("Error getting user profile:", error)
    throw error
  }
}

/**
 * Update user profile
 */
export const updateUserProfile = async (userId, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.USERS, userId), {
      ...updates,
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error("Error updating user profile:", error)
    throw error
  }
}

// ============================================================================
// STUDIO MANAGEMENT
// ============================================================================

/**
 * Create a new studio
 */
export const createStudio = async (studioData, ownerId) => {
  try {
    // Create studio document
    const studioRef = await addDoc(collection(db, COLLECTIONS.STUDIOS), {
      name: studioData.name,
      description: studioData.description || "",
      ownerId: ownerId,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      settings: {
        allowMemberDesignAccess: true,
        requireApprovalForJoining: false,
        ...studioData.settings,
      },
      branding: {
        logo: null,
        primaryColor: "#3B82F6",
        secondaryColor: "#1F2937",
        ...studioData.branding,
      },
    })

    // Add owner as studio member
    await addDoc(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), {
      studioId: studioRef.id,
      userId: ownerId,
      role: STUDIO_ROLES.OWNER,
      joinedAt: serverTimestamp(),
      invitedBy: ownerId,
    })

    return studioRef.id
  } catch (error) {
    console.error("Error creating studio:", error)
    throw error
  }
}

/**
 * Get studio by ID
 */
export const getStudio = async studioId => {
  try {
    const studioDoc = await getDoc(doc(db, COLLECTIONS.STUDIOS, studioId))
    if (studioDoc.exists()) {
      return { id: studioDoc.id, ...studioDoc.data() }
    }
    return null
  } catch (error) {
    console.error("Error getting studio:", error)
    throw error
  }
}

/**
 * Update studio
 */
export const updateStudio = async (studioId, updates) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.STUDIOS, studioId), {
      ...updates,
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error("Error updating studio:", error)
    throw error
  }
}

/**
 * Get studios where user is a member
 */
export const getUserStudios = async userId => {
  try {
    // Get user's memberships
    const membershipsQuery = query(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), where("userId", "==", userId))
    const membershipsSnapshot = await getDocs(membershipsQuery)

    const studios = []
    for (const membershipDoc of membershipsSnapshot.docs) {
      const membership = membershipDoc.data()
      const studio = await getStudio(membership.studioId)
      if (studio) {
        studios.push({
          ...studio,
          userRole: membership.role,
          joinedAt: membership.joinedAt,
        })
      }
    }

    return studios
  } catch (error) {
    console.error("Error getting user studios:", error)
    throw error
  }
}

// ============================================================================
// STUDIO MEMBERSHIP MANAGEMENT
// ============================================================================

/**
 * Add user to studio
 */
export const addStudioMember = async (studioId, userId, role = STUDIO_ROLES.MEMBER, invitedBy) => {
  try {
    // Check if membership already exists
    const existingQuery = query(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), where("studioId", "==", studioId), where("userId", "==", userId))
    const existingSnapshot = await getDocs(existingQuery)

    if (!existingSnapshot.empty) {
      throw new Error("User is already a member of this studio")
    }

    await addDoc(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), {
      studioId,
      userId,
      role,
      joinedAt: serverTimestamp(),
      invitedBy,
    })
  } catch (error) {
    console.error("Error adding studio member:", error)
    throw error
  }
}

/**
 * Remove user from studio
 */
export const removeStudioMember = async (studioId, userId) => {
  try {
    const membershipQuery = query(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), where("studioId", "==", studioId), where("userId", "==", userId))
    const membershipSnapshot = await getDocs(membershipQuery)

    for (const membershipDoc of membershipSnapshot.docs) {
      await deleteDoc(membershipDoc.ref)
    }
  } catch (error) {
    console.error("Error removing studio member:", error)
    throw error
  }
}

/**
 * Update studio member role
 */
export const updateStudioMemberRole = async (studioId, userId, newRole) => {
  try {
    const membershipQuery = query(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), where("studioId", "==", studioId), where("userId", "==", userId))
    const membershipSnapshot = await getDocs(membershipQuery)

    for (const membershipDoc of membershipSnapshot.docs) {
      await updateDoc(membershipDoc.ref, { role: newRole })
    }
  } catch (error) {
    console.error("Error updating studio member role:", error)
    throw error
  }
}

/**
 * Get studio members
 */
export const getStudioMembers = async studioId => {
  try {
    const membershipsQuery = query(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), where("studioId", "==", studioId))
    const membershipsSnapshot = await getDocs(membershipsQuery)

    const members = []
    for (const membershipDoc of membershipsSnapshot.docs) {
      const membership = membershipDoc.data()
      const userProfile = await getUserProfile(membership.userId)
      if (userProfile) {
        members.push({
          ...userProfile,
          role: membership.role,
          joinedAt: membership.joinedAt,
          membershipId: membershipDoc.id,
        })
      }
    }

    return members
  } catch (error) {
    console.error("Error getting studio members:", error)
    throw error
  }
}

/**
 * Check if user has permission in studio
 */
export const hasStudioPermission = async (studioId, userId, requiredRole) => {
  try {
    const membershipQuery = query(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), where("studioId", "==", studioId), where("userId", "==", userId))
    const membershipSnapshot = await getDocs(membershipQuery)

    if (membershipSnapshot.empty) {
      return false
    }

    const membership = membershipSnapshot.docs[0].data()
    const roleHierarchy = [STUDIO_ROLES.VIEWER, STUDIO_ROLES.MEMBER, STUDIO_ROLES.ADMIN, STUDIO_ROLES.OWNER]
    const userRoleIndex = roleHierarchy.indexOf(membership.role)
    const requiredRoleIndex = roleHierarchy.indexOf(requiredRole)

    return userRoleIndex >= requiredRoleIndex
  } catch (error) {
    console.error("Error checking studio permission:", error)
    return false
  }
}

// ============================================================================
// DESIGN MANAGEMENT
// ============================================================================

/**
 * Save a design
 */
export const saveDesign = async (designData, userId, studioId = null) => {
  try {
    const designId = `${userId}_${Date.now()}`
    const designDoc = {
      elements: designData.elements,
      name: designData.name || "Untitled Design",
      createdBy: userId,
      studioId: studioId,
      access: studioId ? "studio" : "personal",
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      metadata: {
        version: 1,
        tags: designData.tags || [],
        description: designData.description || "",
      },
    }

    await setDoc(doc(db, COLLECTIONS.DESIGNS, designId), designDoc)
    return designId
  } catch (error) {
    console.error("Error saving design:", error)
    throw error
  }
}

/**
 * Get user's personal designs
 */
export const getUserDesigns = async userId => {
  try {
    const designsQuery = query(
      collection(db, COLLECTIONS.DESIGNS),
      where("createdBy", "==", userId),
      where("access", "==", "personal"),
      orderBy("updatedAt", "desc"),
    )
    const querySnapshot = await getDocs(designsQuery)

    const designs = []
    querySnapshot.forEach(doc => {
      designs.push({
        id: doc.id,
        ...doc.data(),
      })
    })

    return designs
  } catch (error) {
    console.error("Error getting user designs:", error)
    throw error
  }
}

/**
 * Get studio members with their design status
 */
export const getStudioMembersWithDesigns = async (studioId, adminUserId) => {
  try {
    // Check if user is admin
    const isAdmin = await hasStudioPermission(studioId, adminUserId, STUDIO_ROLES.ADMIN)
    if (!isAdmin) {
      throw new Error("User does not have admin access to this studio")
    }

    // Get all studio members
    const members = await getStudioMembers(studioId)

    // Get all studio designs
    const designsQuery = query(
      collection(db, COLLECTIONS.DESIGNS),
      where("studioId", "==", studioId),
      where("access", "==", "studio"),
      orderBy("updatedAt", "desc"),
    )
    const designsSnapshot = await getDocs(designsQuery)

    // Group designs by creator
    const designsByMember = {}
    designsSnapshot.forEach(doc => {
      const design = { id: doc.id, ...doc.data() }
      const createdBy = design.createdBy
      if (!designsByMember[createdBy]) {
        designsByMember[createdBy] = []
      }
      designsByMember[createdBy].push(design)
    })

    // Combine member info with their designs
    const membersWithDesigns = members.map(member => ({
      ...member,
      designs: designsByMember[member.id] || [],
      hasDesigns: (designsByMember[member.id] || []).length > 0,
      designCount: (designsByMember[member.id] || []).length,
      preferredDesign: (designsByMember[member.id] || []).find(d => d.isPreferred) || (designsByMember[member.id] || [])[0] || null,
    }))

    return membersWithDesigns
  } catch (error) {
    console.error("Error getting studio members with designs:", error)
    throw error
  }
}

/**
 * Set preferred design for a member
 */
export const setPreferredDesign = async (designId, userId) => {
  try {
    const designRef = doc(db, COLLECTIONS.DESIGNS, designId)
    const designDoc = await getDoc(designRef)

    if (!designDoc.exists()) {
      throw new Error("Design not found")
    }

    const design = designDoc.data()

    // Check if user owns this design
    if (design.createdBy !== userId) {
      throw new Error("User can only set preference for their own designs")
    }

    // If already preferred, toggle it OFF (un-submit)
    if (design.isPreferred) {
      await updateDoc(designRef, { isPreferred: false })
      return { action: "unsubmitted" }
    }

    // Remove preferred status from other designs by this user in the same studio
    if (design.studioId) {
      const userDesignsQuery = query(
        collection(db, COLLECTIONS.DESIGNS),
        where("studioId", "==", design.studioId),
        where("createdBy", "==", userId),
        where("access", "==", "studio"),
      )
      const userDesignsSnapshot = await getDocs(userDesignsQuery)

      const batch = []
      userDesignsSnapshot.forEach(doc => {
        batch.push(updateDoc(doc.ref, { isPreferred: false }))
      })
      await Promise.all(batch)
    }

    // Set this design as preferred
    await updateDoc(designRef, { isPreferred: true })
    return { action: "submitted" }
  } catch (error) {
    console.error("Error setting preferred design:", error)
    throw error
  }
}

/**
 * Get studio designs that user can access
 */
export const getStudioDesigns = async (studioId, userId) => {
  try {
    // Check if user has access to studio
    const hasAccess = await hasStudioPermission(studioId, userId, STUDIO_ROLES.VIEWER)
    if (!hasAccess) {
      throw new Error("User does not have access to this studio")
    }

    // Check if user is admin/owner (can see all studio designs) or regular member (only own designs)
    const isAdmin = await hasStudioPermission(studioId, userId, STUDIO_ROLES.ADMIN)
    let designsQuery
    if (isAdmin) {
      // Admins and owners can see all studio designs
      designsQuery = query(
        collection(db, COLLECTIONS.DESIGNS),
        where("studioId", "==", studioId),
        where("access", "==", "studio"),
        orderBy("updatedAt", "desc"),
      )
    } else {
      // Regular members can only see their own studio designs
      designsQuery = query(
        collection(db, COLLECTIONS.DESIGNS),
        where("studioId", "==", studioId),
        where("access", "==", "studio"),
        where("createdBy", "==", userId),
        orderBy("updatedAt", "desc"),
      )
    }

    const querySnapshot = await getDocs(designsQuery)

    const designs = []
    querySnapshot.forEach(doc => {
      const designData = doc.data()
      designs.push({
        id: doc.id,
        ...designData,
      })
    })

    return designs
  } catch (error) {
    console.error("Error getting studio designs:", error)
    throw error
  }
}

/**
 * Check if user can edit a design
 */
export const canEditDesign = async (designId, userId) => {
  try {
    const designDoc = await getDoc(doc(db, COLLECTIONS.DESIGNS, designId))
    if (!designDoc.exists()) {
      return false
    }

    const design = designDoc.data()

    // Owner can always edit
    if (design.createdBy === userId) {
      return true
    }

    // For studio designs, check if user has admin/owner role
    if (design.studioId) {
      return await hasStudioPermission(design.studioId, userId, STUDIO_ROLES.ADMIN)
    }

    return false
  } catch (error) {
    console.error("Error checking design edit permission:", error)
    return false
  }
}

/**
 * Update design
 */
export const updateDesign = async (designId, updates, userId) => {
  try {
    const canEdit = await canEditDesign(designId, userId)
    if (!canEdit) {
      throw new Error("User does not have permission to edit this design")
    }

    await updateDoc(doc(db, COLLECTIONS.DESIGNS, designId), {
      ...updates,
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error("Error updating design:", error)
    throw error
  }
}

/**
 * Delete design
 */
export const deleteDesign = async (designId, userId) => {
  try {
    const canEdit = await canEditDesign(designId, userId)
    if (!canEdit) {
      throw new Error("User does not have permission to delete this design")
    }

    await deleteDoc(doc(db, COLLECTIONS.DESIGNS, designId))
  } catch (error) {
    console.error("Error deleting design:", error)
    throw error
  }
}

// ============================================================================
// USER LOOKUP FUNCTIONS
// ============================================================================

/**
 * Find user by email address
 */
export const findUserByEmail = async email => {
  try {
    const usersQuery = query(collection(db, COLLECTIONS.USERS), where("email", "==", email.toLowerCase()))
    const querySnapshot = await getDocs(usersQuery)

    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0]
      return { id: userDoc.id, ...userDoc.data() }
    }

    return null
  } catch (error) {
    console.error("Error finding user by email:", error)
    throw error
  }
}

// ============================================================================
// STUDIO INVITE MANAGEMENT
// ============================================================================

/**
 * Generate a short random invite code
 */
const generateInviteCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789"
  let code = ""
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return code
}

/**
 * Create an invite for a studio (email-based or link-based)
 */
export const createStudioInvite = async (studioId, invitedBy, { email = null, role = STUDIO_ROLES.MEMBER } = {}) => {
  try {
    const code = generateInviteCode()
    const expiresAt = new Date()
    expiresAt.setDate(expiresAt.getDate() + 7)

    const inviteData = {
      studioId,
      inviteCode: code,
      role,
      invitedBy,
      invitedEmail: email ? email.toLowerCase() : null,
      status: INVITE_STATUS.PENDING,
      createdAt: serverTimestamp(),
      expiresAt: expiresAt,
    }

    const inviteRef = await addDoc(collection(db, COLLECTIONS.STUDIO_INVITES), inviteData)
    return { id: inviteRef.id, inviteCode: code, expiresAt }
  } catch (error) {
    console.error("Error creating studio invite:", error)
    throw error
  }
}

/**
 * Get pending invites for a studio
 */
export const getStudioInvites = async studioId => {
  try {
    const invitesQuery = query(
      collection(db, COLLECTIONS.STUDIO_INVITES),
      where("studioId", "==", studioId),
      where("status", "==", INVITE_STATUS.PENDING),
    )
    const snapshot = await getDocs(invitesQuery)

    const invites = []
    snapshot.forEach(doc => {
      const data = doc.data()
      const expiresAt = data.expiresAt?.toDate ? data.expiresAt.toDate() : new Date(data.expiresAt)
      const isExpired = expiresAt < new Date()

      invites.push({
        id: doc.id,
        ...data,
        isExpired,
      })
    })

    return invites
  } catch (error) {
    console.error("Error getting studio invites:", error)
    throw error
  }
}

/**
 * Revoke (cancel) a studio invite
 */
export const revokeStudioInvite = async inviteId => {
  try {
    await updateDoc(doc(db, COLLECTIONS.STUDIO_INVITES, inviteId), {
      status: INVITE_STATUS.REVOKED,
    })
  } catch (error) {
    console.error("Error revoking studio invite:", error)
    throw error
  }
}

/**
 * Accept a studio invite by code
 */
export const acceptStudioInvite = async (inviteCode, userId) => {
  try {
    const inviteQuery = query(
      collection(db, COLLECTIONS.STUDIO_INVITES),
      where("inviteCode", "==", inviteCode),
      where("status", "==", INVITE_STATUS.PENDING),
    )
    const snapshot = await getDocs(inviteQuery)

    if (snapshot.empty) {
      throw new Error("Invite not found or has already been used")
    }

    const inviteDoc = snapshot.docs[0]
    const invite = inviteDoc.data()

    const expiresAt = invite.expiresAt?.toDate ? invite.expiresAt.toDate() : new Date(invite.expiresAt)
    if (expiresAt < new Date()) {
      await updateDoc(inviteDoc.ref, { status: INVITE_STATUS.EXPIRED })
      throw new Error("This invite has expired")
    }

    // Check if user is already a member
    const existingQuery = query(
      collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS),
      where("studioId", "==", invite.studioId),
      where("userId", "==", userId),
    )
    const existingSnapshot = await getDocs(existingQuery)

    if (!existingSnapshot.empty) {
      throw new Error("You are already a member of this studio")
    }

    // Add user to studio
    await addDoc(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), {
      studioId: invite.studioId,
      userId,
      role: invite.role,
      joinedAt: serverTimestamp(),
      invitedBy: invite.invitedBy,
    })

    // Mark invite as accepted
    await updateDoc(inviteDoc.ref, { status: INVITE_STATUS.ACCEPTED, acceptedBy: userId })

    // Get studio name for the success message
    const studio = await getStudio(invite.studioId)
    return { studioName: studio?.name || "Studio", role: invite.role }
  } catch (error) {
    console.error("Error accepting studio invite:", error)
    throw error
  }
}

/**
 * Check for pending invites for a user's email and auto-accept them
 */
export const checkPendingInvitesForEmail = async (email, userId) => {
  try {
    const inviteQuery = query(
      collection(db, COLLECTIONS.STUDIO_INVITES),
      where("invitedEmail", "==", email.toLowerCase()),
      where("status", "==", INVITE_STATUS.PENDING),
    )
    const snapshot = await getDocs(inviteQuery)

    const results = []
    for (const inviteDoc of snapshot.docs) {
      const invite = inviteDoc.data()
      const expiresAt = invite.expiresAt?.toDate ? invite.expiresAt.toDate() : new Date(invite.expiresAt)

      if (expiresAt < new Date()) {
        await updateDoc(inviteDoc.ref, { status: INVITE_STATUS.EXPIRED })
        continue
      }

      try {
        const result = await acceptStudioInvite(invite.inviteCode, userId)
        results.push(result)
      } catch (err) {
        console.error("Error auto-accepting invite:", err)
      }
    }

    return results
  } catch (error) {
    console.error("Error checking pending invites:", error)
    return []
  }
}

/**
 * Delete a studio and all associated data (memberships, invites)
 */
export const deleteStudio = async (studioId, userId) => {
  try {
    const studio = await getStudio(studioId)
    if (!studio) {
      throw new Error("Studio not found")
    }

    if (studio.ownerId !== userId) {
      throw new Error("Only the studio owner can delete the studio")
    }

    // Delete all memberships
    const membershipsQuery = query(collection(db, COLLECTIONS.STUDIO_MEMBERSHIPS), where("studioId", "==", studioId))
    const membershipsSnapshot = await getDocs(membershipsQuery)
    for (const memberDoc of membershipsSnapshot.docs) {
      await deleteDoc(memberDoc.ref)
    }

    // Delete all invites
    const invitesQuery = query(collection(db, COLLECTIONS.STUDIO_INVITES), where("studioId", "==", studioId))
    const invitesSnapshot = await getDocs(invitesQuery)
    for (const inviteDoc of invitesSnapshot.docs) {
      await deleteDoc(inviteDoc.ref)
    }

    // Delete the studio
    await deleteDoc(doc(db, COLLECTIONS.STUDIOS, studioId))
  } catch (error) {
    console.error("Error deleting studio:", error)
    throw error
  }
}


// ============================================================================
// NOTIFICATIONS
// ============================================================================

/**
 * Create a notification for a user
 */
export const createNotification = async (recipientId, { type, title, message, studioId, designId, fromUserId }) => {
  try {
    const notifRef = await addDoc(collection(db, COLLECTIONS.NOTIFICATIONS), {
      recipientId,
      type,
      title,
      message,
      studioId: studioId || null,
      designId: designId || null,
      fromUserId: fromUserId || null,
      read: false,
      createdAt: serverTimestamp(),
    })
    return notifRef.id
  } catch (error) {
    console.error("Error creating notification:", error)
    throw error
  }
}

/**
 * Get unread notifications for a user (real-time listener)
 */
export const subscribeToNotifications = (userId, callback) => {
  const q = query(
    collection(db, COLLECTIONS.NOTIFICATIONS),
    where("recipientId", "==", userId)
  )

  // Use onSnapshot for real-time updates
  // Sort client-side to avoid needing a composite Firestore index
  return onSnapshot(q, (snapshot) => {
    const notifications = []
    snapshot.forEach(doc => {
      notifications.push({ id: doc.id, ...doc.data() })
    })
    notifications.sort((a, b) => {
      const aTime = a.createdAt?.toDate?.() || new Date(0)
      const bTime = b.createdAt?.toDate?.() || new Date(0)
      return bTime - aTime
    })
    callback(notifications)
  }, (error) => {
    console.error("Notification subscription error:", error)
    if (error.message && error.message.includes("index")) {
      console.error("Create the required Firestore index using the URL above")
    }
    callback([])
  })
}

/**
 * Mark a single notification as read
 */
export const markNotificationRead = async (notificationId) => {
  try {
    await updateDoc(doc(db, COLLECTIONS.NOTIFICATIONS, notificationId), { read: true })
  } catch (error) {
    console.error("Error marking notification read:", error)
    throw error
  }
}

/**
 * Mark all notifications as read for a user
 */
export const markAllNotificationsRead = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.NOTIFICATIONS),
      where("recipientId", "==", userId)
    )
    const snapshot = await getDocs(q)
    const updates = []
    snapshot.forEach(doc => {
      if (!doc.data().read) {
        updates.push(updateDoc(doc.ref, { read: true }))
      }
    })
    await Promise.all(updates)
  } catch (error) {
    console.error("Error marking all notifications read:", error)
    throw error
  }
}